// src/components/private/EditUserModal.js
import React, { useState } from 'react';
import { Modal, Button, Form, Col, Row, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { COUNTRY_CHOICES } from '../common_components/choices';

const EditUserModal = ({ show, handleClose, user, fetchUserInfo }) => {
    const [formValues, setFormValues] = useState({
        address_line_1: user.address_line_1 || '',
        postal_code: user.postal_code || '',
        city: user.city || '',
        state: user.state || '',
        country: user.country || '',
        phone_number: user.phone_number || '',
        geo_location: user.geo_location || null,
    });
    
    const [isValidateMode, setIsValidateMode] = useState(!user.has_geo_location); // If no geo_location, start in validate mode
    const [isValidating, setIsValidating] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [validationError, setValidationError] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };

    const handleValidateAddress = async () => {
        if (!formValues.address_line_1.trim()) {
            setValidationError('Please enter an address to validate');
            return;
        }

        setIsValidating(true);
        setValidationError('');
        setValidationMessage('');

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_API_URL}/users/validate-address/`,
                { address: formValues.address_line_1 },
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.success) {
                // Auto-fill the form with validated data
                setFormValues({
                    ...formValues,
                    address_line_1: response.data.street,
                    city: response.data.city,
                    state: response.data.state,
                    postal_code: response.data.zip_code,
                    country: response.data.country_code,
                    geo_location: {
                        type: 'Point',
                        coordinates: [response.data.longitude, response.data.latitude]
                    }
                });
                setValidationMessage(`✓ Address validated: ${response.data.display_name}`);
                setIsValidateMode(false); // Switch to edit mode after successful validation
            } else {
                setValidationError(response.data.error || 'Could not validate address');
            }
        } catch (err) {
            console.error('Error validating address:', err);
            setValidationError(
                err.response?.data?.error ||
                'Failed to validate address. Please try again.'
            );
        } finally {
            setIsValidating(false);
        }
    };

    const handleToggleMode = () => {
        if (isValidateMode) {
            // Currently in validate mode, clicking validates
            handleValidateAddress();
        } else {
            // Currently in edit mode, clicking enables validate mode
            setIsValidateMode(true);
            setValidationMessage('');
            setValidationError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`${process.env.REACT_APP_BACKEND_API_URL}/users/users/${user.username}/`, formValues, {
                headers: {
                    Authorization: `Token ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            fetchUserInfo(); // Assuming fetchUserInfo() is passed down and will update the main component's state
            handleClose();
        } catch (error) {
            console.error('Error updating user info:', error.response ? error.response.data : error);
        }
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Edit Information</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    {/* Address Validation Section */}
                    {!user.has_geo_location && (
                        <Alert variant="info">
                            <strong>📍 Validate your address</strong> to get personalized offers and nearby stores!
                        </Alert>
                    )}
                    
                    {validationError && <Alert variant="danger">{validationError}</Alert>}
                    {validationMessage && <Alert variant="success">{validationMessage}</Alert>}
                    
                    <Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="text"
                                    name="address_line_1"
                                    placeholder="e.g., Sewanstraße 4, 10319 Berlin"
                                    value={formValues.address_line_1}
                                    onChange={handleInputChange}
                                    disabled={!isValidateMode && !isValidating}
                                />
                                <Button
                                    variant={isValidateMode ? "primary" : "outline-secondary"}
                                    onClick={handleToggleMode}
                                    disabled={isValidating || (isValidateMode && !formValues.address_line_1.trim())}
                                    style={{ minWidth: '100px' }}
                                >
                                    {isValidating ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : isValidateMode ? (
                                        '🌍 Validate'
                                    ) : (
                                        '✏️ Edit'
                                    )}
                                </Button>
                            </div>
                            <Form.Text className="text-muted">
                                {isValidateMode 
                                    ? 'Enter your complete address and click Validate'
                                    : 'Click Edit to change your address'
                                }
                            </Form.Text>
                        </Form.Group>
                    </Row>

                    <hr />
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Postal Code</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="postal_code" 
                                    value={formValues.postal_code} 
                                    onChange={handleInputChange}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>City</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="city" 
                                    value={formValues.city} 
                                    onChange={handleInputChange}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>State</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="state" 
                                    value={formValues.state} 
                                    onChange={handleInputChange}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Country</Form.Label>
                                <Form.Select
                                    aria-label="Country"
                                    name="country"
                                    value={formValues.country}
                                    onChange={handleInputChange}
                                    required
                                    disabled
                                >
                                    {COUNTRY_CHOICES.map((country) => (
                                        <option key={country.value} value={country.value}>
                                            {country.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    {/* 
                    <Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control type="text" name="phone_number" value={formValues.phone_number} onChange={handleInputChange} />
                        </Form.Group>
                    </Row>
                    */}
                    <Row>
                        <Col xs="auto">
                            <Button variant="primary" type="submit">
                                Save Changes
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EditUserModal;
